import React, { FC, useEffect } from 'react';
import { Load } from '../../../types';
import { OrgQueryKeys } from '../../../api/config';
import { useUser } from '../../../api/queries/use-user';
import { useQueryClient } from '@tanstack/react-query';
import { findUnknownPairsInCache, nonHookNonLoadTravelTime, nonHookTravelTime, TravelTimesCache, updatePointlessPairs } from '../../travel-times/use-travel-times';
import { useAddTravelTimes } from '../../../api/mutations/reworked/add/use-add-travel-times';
import { orderToLoadingDurationMinutes } from '../../../utils/data-mapping-utils';
import { getDuration } from '../../../utils/date-time-utils';
import { CircularProgress } from '@mui/material';

interface RunningTimeProps {
    selectedLoads: Load[];
    driverHomeLocationId?: number;
}

export const RunningTime: FC<RunningTimeProps> = (props) => {
    const { selectedLoads, driverHomeLocationId } = props;
    const { organizationId } = useUser();
    const queryKey = OrgQueryKeys.resolve(organizationId, OrgQueryKeys.locationTravelTimes);
    const queryClient = useQueryClient();
    const travelTimesCache = queryClient.getQueryData<TravelTimesCache>([queryKey]);
    const { mutate: addTravelTimes, isLoading } = useAddTravelTimes(organizationId);

    useEffect(() => {
        // should always have travelTimesCache at this point
        // isLoading check is for some reason selectedLoads changes x2
        // probably related to the way we are doing order loads are selected? not sure
        if (travelTimesCache && !isLoading) {
            const pairs = selectedLoads.map((load, i) => {
                if (i !== 0) {
                    const previousLoadStops = selectedLoads[i - 1].stops;
                    const previousLoadsLastLocationId = previousLoadStops[previousLoadStops.length - 1].organizationLocationId;
                    const firstStopLocationId = load.stops[0].organizationLocationId;

                    const pair = [previousLoadsLastLocationId, firstStopLocationId];
                    return pair;
                }
            }).filter(x => !!x);

            if (driverHomeLocationId) {
                const firstStopLocationId = selectedLoads[0].stops[0].organizationLocationId;

                const lastLoad = selectedLoads[selectedLoads.length - 1];
                const lastStopLocationId = lastLoad.stops[lastLoad.stops.length - 1].organizationLocationId;

                pairs.unshift([driverHomeLocationId, firstStopLocationId]);
                pairs.push([lastStopLocationId, driverHomeLocationId]);
            }

            const unknowPairs = findUnknownPairsInCache(travelTimesCache, pairs);
            const filteredUnknowPairs = updatePointlessPairs(unknowPairs, travelTimesCache, queryClient, queryKey);

            if (filteredUnknowPairs.length) {
                addTravelTimes(unknowPairs);
            }
        }
    }, [selectedLoads]);

    function getTravelTime(fromLocationId: number, toLocationId: number) {
        if (fromLocationId === toLocationId) return 0;

        const key = [`${fromLocationId},${toLocationId}`];
        const cachedTravelTime = nonHookNonLoadTravelTime(travelTimesCache, key);

        return cachedTravelTime;
    }

    const allTheTimes = selectedLoads.map((load, i) => {
        let travelTimeBetweenLoads = 0;

        const loadUnloadDuration = orderToLoadingDurationMinutes(load) as number;

        if (i === 0 && driverHomeLocationId) {
            const firstStopLocationId = load.stops[0].organizationLocationId;
            travelTimeBetweenLoads = getTravelTime(driverHomeLocationId, firstStopLocationId);
        } else if (i !== 0) {
            const previousLoadStops = selectedLoads[i - 1].stops;
            const previousLoadsLastLocationId = previousLoadStops[previousLoadStops.length - 1].organizationLocationId;
            const firstStopLocationId = load.stops[0].organizationLocationId;
            travelTimeBetweenLoads = getTravelTime(previousLoadsLastLocationId, firstStopLocationId);

            // if this is the last load and we have a driverHomeLocationId, add the travel time back to home
            if (driverHomeLocationId && i === selectedLoads.length - 1) {
                const lastStopLocationId = load.stops[load.stops.length - 1].organizationLocationId;
                travelTimeBetweenLoads += getTravelTime(lastStopLocationId, driverHomeLocationId);
            }
        }

        const loadTravelTime = nonHookTravelTime(travelTimesCache, load);

        return loadTravelTime + travelTimeBetweenLoads + loadUnloadDuration;
    });

    return isLoading ? <CircularProgress size='1em' /> : <RunningTimeInternal allTheTimes={allTheTimes} />;
};

interface RunningTimeInternal {
    allTheTimes: number[];
}

const RunningTimeInternal: FC<RunningTimeInternal> = (props) => {
    const { allTheTimes } = props;

    const sumTimes = allTheTimes.reduce((prev, curr) => prev + curr, 0);
    const minuteMs = 1000 * 60;
    const duration = getDuration(sumTimes * minuteMs);

    return duration;
}
